import type { BasicPreBookingFragment, BasicPresenceFragment } from '@drycut/graphql';

import { buttonVariants, Para, Stack } from '@drycut/ui-components';
import { Field, Radio, RadioGroup } from '@headlessui/react';
import { DateTime } from 'luxon';

import { useCheckoutStore } from '../../checkoutStore';
import { buildPresenceSlots, extendSlotsToSingleSchedule } from './utils';

type Props = {
  onSelect: (date: DateTime) => void;
  preBookings: BasicPreBookingFragment[];
  presences: BasicPresenceFragment[];
  selectedDate: DateTime;
};

export default function SlotSelect({
  selectedDate,
  preBookings,
  onSelect,
  presences,
}: Props): React.ReactElement {
  const selectedStore = useCheckoutStore((state) => state.selectedStore);

  // Returns an array of DaySlot objects representing presence slots for the selected date
  const presenceSlots = buildPresenceSlots(selectedDate, presences);

  // Returns a single DaySchedule covering all presence slots (with gaps included) so we can have
  // continuous time slots for booking (we need to disable the "gaps" later)
  const daySchedule = extendSlotsToSingleSchedule(selectedDate, presenceSlots);

  // Interval in minutes before you can book a slot (based on current time)
  const INTERVAL_BEFORE_BOOKING = 45;

  // Time in hourse to buffer from the start of the schedule
  const BOOKING_START_BUFFER_HOURS = 2;

  // Time in hours to buffer from the end of the schedule
  const BOOKING_END_BUFFER_HOURS = 1;

  // Build time slots for the day in 1 hour intervals
  const timeSlots = [];
  const bookingWindowStart = daySchedule.start.plus({ hours: BOOKING_START_BUFFER_HOURS });
  const bookingWindowEnd = daySchedule.end.minus({ minutes: BOOKING_END_BUFFER_HOURS });

  let currentTime = bookingWindowStart;
  while (currentTime <= bookingWindowEnd) {
    timeSlots.push(currentTime);
    currentTime = currentTime.plus({ hours: 1 });
  }

  const isWithinBookingWindow = (time: DateTime) => {
    const withinWorkingHours = time >= bookingWindowStart && time <= bookingWindowEnd;
    const inPresenceSlot = presenceSlots.some((slot) => time >= slot.start && time < slot.end);
    return withinWorkingHours && inPresenceSlot;
  };

  const isBooked = (time: DateTime) => {
    return preBookings.some((booking) => {
      const bookingStart = DateTime.fromISO(booking.bookTime).startOf('minute');
      return time.startOf('minute').equals(bookingStart);
    });
  };

  if (timeSlots.length === 0) {
    return (
      <Para align="center" pretty className="opacity-40">
        Für dieses Datum sind keine Zeitfenster verfügbar. Versuchen Sie, einen anderen Tag
        auszuwählen.
      </Para>
    );
  }

  return (
    <RadioGroup
      value={selectedDate.toISO()}
      onChange={(value) => onSelect(DateTime.fromISO(value as string))}
      className="grid grid-cols-2 w-full gap-3"
    >
      {timeSlots.map((time) => {
        const disabled =
          !isWithinBookingWindow(time) ||
          time.diff(DateTime.now(), 'minutes').minutes <
            INTERVAL_BEFORE_BOOKING + Number(selectedStore?.waitingTime) / 60 ||
          isBooked(time);

        const { wrapper, text } = buttonVariants({
          fill: 'ghost',
          disabled,
        });

        return (
          <Field key={time.toISO()} className="flex">
            <Radio
              value={time.toISO()}
              disabled={disabled}
              className={wrapper({
                className:
                  'font-aribau bg-gray-200 hover:bg-primary-200 aria-disabled:hover:bg-gray-200 w-full p-3',
              })}
            >
              <Stack direction="row" className={text({ className: 'text-sm' })}>
                <Para size="sm" weight="bold" className="-me-2 hidden sm:flex">
                  ca.
                </Para>

                <Para size="sm" weight="bold" className="-me-3 sm:hidden">
                  ~
                </Para>

                <Para size="sm" weight="bold">
                  {time.toFormat('HH:mm')}
                </Para>

                <Para size="xs" className="-ms-2 sm:-ms-0.5 sm:text-sm">
                  (+/- 10')
                </Para>
              </Stack>
            </Radio>
          </Field>
        );
      })}
    </RadioGroup>
  );
}
